import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import userService from '../../services/userService';
import Pagination from '../../components/Pagination/Pagination';
import './UserManagePage.css';

const UserManagePage = () => {
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const [activeMenu, setActiveMenu] = useState('manage');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    byStatus: { active: 0, inactive: 0, suspended: 0 },
    byType: { buyers: 0, suppliers: 0 },
    byRole: { admins: 0, subAdmins: 0, regularUsers: 0 }
  });

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    companyName: '',
    phone: '',
    country: '',
    role: 'Sales Manager',
    adminRole: 'user',
    status: 'active',
    isBuyer: true,
    isSupplier: false
  });

  const [formErrors, setFormErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', path: '/admin-dashboard' },
    { id: 'news', label: 'News Lists', icon: 'list', hasSubmenu: true },
    { id: 'manage', label: 'Manage', icon: 'manage', hasSubmenu: true },
    { id: 'payment', label: 'Payment History', icon: 'payment', path: '/payment-history' },
    { id: 'location', label: 'Location & Category', icon: 'location', path: '/location-category' },
    { id: 'verification', label: 'Verification manage', icon: 'verification' },
    { id: 'membership', label: 'Membership Plan', icon: 'membership', hasSubmenu: true },
    { id: 'ads', label: 'Ads & Promotion', icon: 'ads' },
    { id: 'roles', label: 'Roles & permissions', icon: 'roles', hasSubmenu: true, path: '/sub-admin' },
    { id: 'support', label: 'Support', icon: 'support' },
    { id: 'brand', label: 'Add Brand', icon: 'brand', path: '/add-brand' },
    { id: 'subscriber', label: 'Subscriber', icon: 'subscriber' },
    { id: 'profile', label: 'Profile Settings', icon: 'profile', path: '/profile' },
    { id: 'settings', label: 'Settings', icon: 'settings', hasSubmenu: true },
    { id: 'logout', label: 'Log out', icon: 'logout', isLogout: true },
  ];

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search,
        status: statusFilter,
        adminRole: roleFilter
      };

      if (typeFilter === 'buyer') {
        params.isBuyer = 'true';
      } else if (typeFilter === 'supplier') {
        params.isSupplier = 'true';
      }

      const data = await userService.getUsers(params);
      setUsers(data.users || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        setAlert({ show: true, type: 'error', message: 'You do not have permission to view users. Please login as admin.' });
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, roleFilter, typeFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await userService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers, fetchStats]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page !== 1) setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Validate form
  const validateForm = (isEdit = false) => {
    const errors = {};
    
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (!isEdit && !formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form change
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle add user
  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      await userService.createUser(formData);
      setAlert({ show: true, type: 'success', message: 'User created successfully!' });
      setShowAddModal(false);
      resetForm();
      fetchUsers();
      fetchStats();
    } catch (error) {
      setAlert({ 
        show: true, 
        type: 'error', 
        message: error.response?.data?.error || 'Error creating user' 
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle edit user
  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!validateForm(true)) return;

    setSaving(true);
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      await userService.updateUser(selectedUser.id, updateData);
      setAlert({ show: true, type: 'success', message: 'User updated successfully!' });
      setShowEditModal(false);
      resetForm();
      fetchUsers();
      fetchStats();
    } catch (error) {
      setAlert({ 
        show: true, 
        type: 'error', 
        message: error.response?.data?.error || 'Error updating user' 
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    setSaving(true);
    try {
      await userService.deleteUser(selectedUser.id);
      setAlert({ show: true, type: 'success', message: 'User deleted successfully!' });
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
      fetchStats();
    } catch (error) {
      setAlert({ 
        show: true, 
        type: 'error', 
        message: error.response?.data?.error || 'Error deleting user' 
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (userId, newStatus) => {
    try {
      await userService.updateStatus(userId, newStatus);
      setAlert({ show: true, type: 'success', message: 'Status updated successfully!' });
      fetchUsers();
      fetchStats();
    } catch (error) {
      setAlert({ 
        show: true, 
        type: 'error', 
        message: error.response?.data?.error || 'Error updating status' 
      });
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      await userService.exportUsers();
      setAlert({ show: true, type: 'success', message: 'Users exported successfully!' });
    } catch (error) {
      setAlert({ show: true, type: 'error', message: 'Error exporting users' });
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      companyName: '',
      phone: '',
      country: '',
      role: 'Sales Manager',
      adminRole: 'user',
      status: 'active',
      isBuyer: true,
      isSupplier: false
    });
    setFormErrors({});
    setSelectedUser(null);
  };

  // Open edit modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email || '',
      password: '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      companyName: user.companyName || '',
      phone: user.phone || '',
      country: user.country || '',
      role: user.role || 'Sales Manager',
      adminRole: user.adminRole || 'user',
      status: user.status || 'active',
      isBuyer: user.isBuyer || false,
      isSupplier: user.isSupplier || false
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Open view modal
  const openViewModal = async (user) => {
    try {
      const data = await userService.getUser(user.id);
      setSelectedUser(data.user);
      setShowViewModal(true);
    } catch (error) {
      setAlert({ show: true, type: 'error', message: 'Error fetching user details' });
    }
  };

  // Get initials
  const getInitials = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.companyName) {
      return user.companyName.substring(0, 2).toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  // Get display name
  const getDisplayName = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.companyName) {
      return user.companyName;
    }
    return user.email.split('@')[0];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle menu click
  const handleMenuClick = (item) => {
    if (item.isLogout) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/');
      return;
    }
    if (item.path) {
      navigate(item.path);
    }
    setActiveMenu(item.id);
  };

  // Clear alert after 3 seconds
  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert({ show: false, type: '', message: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert.show]);

  return (
    <div className="user-manage-page">
      {/* Header */}
      <header className="user-manage-header">
        <div className="header-content">
          <img 
            src="https://api.builder.io/api/v1/image/assets/TEMP/9b0cfe564352e0eb74eaa180cf676b171fa47e19?width=532" 
            alt="Gulf Constructor Logo" 
            className="logo"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
          <div className="header-right">
            <div className="language-selector">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 18.333A8.333 8.333 0 1 0 10 1.667a8.333 8.333 0 0 0 0 16.666Z" stroke="#354556" strokeWidth="1.25"/>
                <path d="M1.667 10h16.666M10 1.667A12.75 12.75 0 0 1 13.333 10 12.75 12.75 0 0 1 10 18.333 12.75 12.75 0 0 1 6.667 10 12.75 12.75 0 0 1 10 1.667Z" stroke="#354556" strokeWidth="1.25"/>
              </svg>
              <span>عربي</span>
            </div>
            <div className="user-profile">
              <div className="user-avatar">
                {currentUser?.firstName?.[0] || currentUser?.email?.[0]?.toUpperCase() || 'A'}
              </div>
              <div className="user-info">
                <span className="user-name">{currentUser?.firstName || currentUser?.companyName || 'Admin'}</span>
                <span className="user-role">{currentUser?.adminRole === 'admin' ? 'Admin' : currentUser?.adminRole === 'subadmin' ? 'Sub Admin' : 'User'}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="user-manage-body">
        {/* Sidebar */}
        <aside className="user-manage-sidebar">
          <div className="sidebar-profile">
            <div className="profile-avatar-wrapper">
              <div className="profile-avatar">
                {currentUser?.firstName?.[0] || currentUser?.email?.[0]?.toUpperCase() || 'A'}
              </div>
              <div className="edit-badge">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.333 2A1.886 1.886 0 0 1 14 4.667l-9 9-3.667 1 1-3.667 9-9Z" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className="profile-info">
              <span className="profile-name">{currentUser?.firstName || currentUser?.companyName || 'Admin User'}</span>
              <span className="profile-role">{currentUser?.adminRole === 'admin' ? 'Admin' : currentUser?.adminRole === 'subadmin' ? 'Sub Admin' : 'User'}</span>
            </div>
          </div>

          <nav className="sidebar-nav">
            {menuItems.map((item) => (
              <div 
                key={item.id} 
                className={`nav-item ${activeMenu === item.id ? 'active' : ''} ${item.isLogout ? 'logout' : ''}`}
                onClick={() => handleMenuClick(item)}
              >
                <div className="nav-item-left">
                  <span className="nav-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2Z" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </span>
                  <span className="nav-label">{item.label}</span>
                </div>
                {item.hasSubmenu && (
                  <svg className="chevron" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="user-manage-main">
          {/* Alert */}
          {alert.show && (
            <div className={`alert ${alert.type}`}>
              {alert.message}
            </div>
          )}

          {/* Page Header */}
          <div className="page-header">
            <div className="header-text">
              <h1>User Management</h1>
              <p>View, add, edit, and manage all user accounts with customized roles and permissions.</p>
            </div>
            <div className="header-actions">
              <button className="export-btn" onClick={handleExport}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Export CSV</span>
              </button>
              <button className="add-user-btn" onClick={() => { resetForm(); setShowAddModal(true); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>Add New User</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card highlight">
              <div className="stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="stat-label">Total Users</div>
              <div className="stat-value">{stats.total}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#2E7D32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 4 12 14.01l-3-3" stroke="#2E7D32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="stat-label">Active Users</div>
              <div className="stat-value">{stats.byStatus?.active || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M14 4h6M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke="#0277BD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="stat-label">Buyers</div>
              <div className="stat-value">{stats.byType?.buyers || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M20 8v6M23 11h-6M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke="#F57F17" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="stat-label">Suppliers</div>
              <div className="stat-value">{stats.byType?.suppliers || 0}</div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="filters-section">
            <div className="filters-row">
              <div className="filter-group search">
                <label>Search</label>
                <div className="search-input-wrapper">
                  <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" stroke="#999" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name, email, phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="filter-group">
                <label>Status</label>
                <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Role</label>
                <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
                  <option value="">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="subadmin">Sub Admin</option>
                  <option value="user">User</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Type</label>
                <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
                  <option value="">All Types</option>
                  <option value="buyer">Buyer</option>
                  <option value="supplier">Supplier</option>
                </select>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="users-table-container">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="empty-state">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#ccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3>No Users Found</h3>
                <p>Try adjusting your search or filter criteria, or login as admin to view users.</p>
              </div>
            ) : (
              <>
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Phone</th>
                      <th>Country</th>
                      <th>Type</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="user-cell">
                            <div className="user-table-avatar">
                              {user.avatar ? (
                                <img src={user.avatar} alt={getDisplayName(user)} />
                              ) : (
                                getInitials(user)
                              )}
                            </div>
                            <div className="user-table-info">
                              <span className="user-table-name">{getDisplayName(user)}</span>
                              <span className="user-table-email">{user.email}</span>
                            </div>
                          </div>
                        </td>
                        <td>{user.phone || '-'}</td>
                        <td>{user.country || '-'}</td>
                        <td>
                          <div className="type-badges">
                            {user.isBuyer && <span className="type-badge buyer">Buyer</span>}
                            {user.isSupplier && <span className="type-badge supplier">Supplier</span>}
                            {!user.isBuyer && !user.isSupplier && <span className="type-badge">-</span>}
                          </div>
                        </td>
                        <td>
                          <span className={`role-badge ${user.adminRole}`}>
                            {user.adminRole === 'admin' ? 'Admin' : user.adminRole === 'subadmin' ? 'Sub Admin' : 'User'}
                          </span>
                        </td>
                        <td>
                          <select 
                            className={`status-select ${user.status}`}
                            value={user.status}
                            onChange={(e) => handleStatusChange(user.id, e.target.value)}
                            disabled={user.id === currentUser?.id}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="suspended">Suspended</option>
                          </select>
                        </td>
                        <td>{formatDate(user.createdAt)}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="action-btn view" title="View Details" onClick={() => openViewModal(user)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" stroke="currentColor" strokeWidth="2"/>
                                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                            </button>
                            <button className="action-btn edit" title="Edit" onClick={() => openEditModal(user)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5Z" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                            </button>
                            <button className="action-btn delete" title="Delete" onClick={() => { setSelectedUser(user); setShowDeleteModal(true); }} disabled={user.id === currentUser?.id}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14Z" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} users
                  </div>
                  <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New User</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleAddUser}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Email <span>*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleFormChange} className={formErrors.email ? 'error' : ''} />
                    {formErrors.email && <span className="form-error">{formErrors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label>Password <span>*</span></label>
                    <input type="password" name="password" value={formData.password} onChange={handleFormChange} className={formErrors.password ? 'error' : ''} />
                    {formErrors.password && <span className="form-error">{formErrors.password}</span>}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>First Name</label><input type="text" name="firstName" value={formData.firstName} onChange={handleFormChange} /></div>
                  <div className="form-group"><label>Last Name</label><input type="text" name="lastName" value={formData.lastName} onChange={handleFormChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Company Name</label><input type="text" name="companyName" value={formData.companyName} onChange={handleFormChange} /></div>
                  <div className="form-group"><label>Phone</label><input type="text" name="phone" value={formData.phone} onChange={handleFormChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Country</label><input type="text" name="country" value={formData.country} onChange={handleFormChange} /></div>
                  <div className="form-group"><label>Job Title</label><input type="text" name="role" value={formData.role} onChange={handleFormChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Admin Role</label>
                    <select name="adminRole" value={formData.adminRole} onChange={handleFormChange}>
                      <option value="user">User</option>
                      <option value="subadmin">Sub Admin</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" value={formData.status} onChange={handleFormChange}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>User Type</label>
                  <div className="checkbox-group">
                    <label className="checkbox-item"><input type="checkbox" name="isBuyer" checked={formData.isBuyer} onChange={handleFormChange} /><span>Buyer</span></label>
                    <label className="checkbox-item"><input type="checkbox" name="isSupplier" checked={formData.isSupplier} onChange={handleFormChange} /><span>Supplier</span></label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Creating...' : 'Add User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleEditUser}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Email <span>*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleFormChange} className={formErrors.email ? 'error' : ''} />
                    {formErrors.email && <span className="form-error">{formErrors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label>New Password (leave blank to keep)</label>
                    <input type="password" name="password" value={formData.password} onChange={handleFormChange} className={formErrors.password ? 'error' : ''} />
                    {formErrors.password && <span className="form-error">{formErrors.password}</span>}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>First Name</label><input type="text" name="firstName" value={formData.firstName} onChange={handleFormChange} /></div>
                  <div className="form-group"><label>Last Name</label><input type="text" name="lastName" value={formData.lastName} onChange={handleFormChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Company Name</label><input type="text" name="companyName" value={formData.companyName} onChange={handleFormChange} /></div>
                  <div className="form-group"><label>Phone</label><input type="text" name="phone" value={formData.phone} onChange={handleFormChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Country</label><input type="text" name="country" value={formData.country} onChange={handleFormChange} /></div>
                  <div className="form-group"><label>Job Title</label><input type="text" name="role" value={formData.role} onChange={handleFormChange} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Admin Role</label>
                    <select name="adminRole" value={formData.adminRole} onChange={handleFormChange} disabled={selectedUser?.id === currentUser?.id}>
                      <option value="user">User</option>
                      <option value="subadmin">Sub Admin</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" value={formData.status} onChange={handleFormChange} disabled={selectedUser?.id === currentUser?.id}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>User Type</label>
                  <div className="checkbox-group">
                    <label className="checkbox-item"><input type="checkbox" name="isBuyer" checked={formData.isBuyer} onChange={handleFormChange} /><span>Buyer</span></label>
                    <label className="checkbox-item"><input type="checkbox" name="isSupplier" checked={formData.isSupplier} onChange={handleFormChange} /><span>Supplier</span></label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="user-detail-header">
                <div className="user-detail-avatar">{selectedUser.avatar ? <img src={selectedUser.avatar} alt={getDisplayName(selectedUser)} /> : getInitials(selectedUser)}</div>
                <div className="user-detail-name">
                  <h3>{getDisplayName(selectedUser)}</h3>
                  <p>{selectedUser.email}</p>
                </div>
              </div>
              <div className="user-detail-grid">
                <div className="detail-item"><label>Company</label><span>{selectedUser.companyName || '-'}</span></div>
                <div className="detail-item"><label>Phone</label><span>{selectedUser.phone || '-'}</span></div>
                <div className="detail-item"><label>Country</label><span>{selectedUser.country || '-'}</span></div>
                <div className="detail-item"><label>Job Title</label><span>{selectedUser.role || '-'}</span></div>
                <div className="detail-item"><label>Admin Role</label><span className={`role-badge ${selectedUser.adminRole}`}>{selectedUser.adminRole === 'admin' ? 'Admin' : selectedUser.adminRole === 'subadmin' ? 'Sub Admin' : 'User'}</span></div>
                <div className="detail-item"><label>Status</label><span className={`status-badge ${selectedUser.status}`}>{selectedUser.status?.charAt(0).toUpperCase() + selectedUser.status?.slice(1)}</span></div>
                <div className="detail-item"><label>User Type</label><div className="type-badges">{selectedUser.isBuyer && <span className="type-badge buyer">Buyer</span>}{selectedUser.isSupplier && <span className="type-badge supplier">Supplier</span>}</div></div>
                <div className="detail-item"><label>Email Verified</label><span>{selectedUser.isEmailVerified ? 'Yes' : 'No'}</span></div>
                <div className="detail-item"><label>Joined</label><span>{formatDate(selectedUser.createdAt)}</span></div>
                <div className="detail-item"><label>Last Login</label><span>{formatDate(selectedUser.lastLoginAt)}</span></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowViewModal(false)}>Close</button>
              <button type="button" className="btn-save" onClick={() => { setShowViewModal(false); openEditModal(selectedUser); }}>Edit User</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>Delete User</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{selectedUser?.email}</strong>?</p>
              <p style={{ color: '#D32F2F', fontSize: '14px' }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button type="button" className="btn-save" style={{ background: '#D32F2F' }} onClick={handleDeleteUser} disabled={saving}>{saving ? 'Deleting...' : 'Delete User'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagePage;
